import { ConfirmationDialog, cn } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import type { NoticeFlag } from "./types";

export const NOTICE_FLAG_LABELS: Record<NoticeFlag, string> = {
  nouvel_indice: "Nouvel indice",
  stand_by: "Stand-by",
  obsolete: "Obsolète",
  new: "New",
};

export const NOTICE_FLAG_CLASSES: Record<NoticeFlag, string> = {
  nouvel_indice: "bg-red-600 text-white",
  stand_by: "border border-zinc-300 text-orange-600 dark:border-zinc-600",
  obsolete:
    "border border-zinc-300 text-zinc-500 dark:border-zinc-600 dark:text-zinc-400",
  new: "border border-zinc-300 text-zinc-700 dark:border-zinc-600 dark:text-zinc-300",
};

type NoticeFlagChoice = Exclude<NoticeFlag, "new">;

// « New » (version à analyser) is shown, never chosen.
const NOTICE_FLAG_CHOICES: NoticeFlagChoice[] = [
  "nouvel_indice",
  "stand_by",
  "obsolete",
];

// Options would otherwise inherit the badge colours on some platforms.
const OPTION_CLASS =
  "bg-white text-zinc-900 dark:bg-zinc-900 dark:text-zinc-100";

export type NoticeFlagSelectProps = {
  yardId: string;
  documentId: number;
  /** « N° - intitulé », named in the confirmation window. */
  documentLabel: string;
  value: NoticeFlag;
  /** Called once the new instruction is saved. */
  onChanged: () => void;
};

/**
 * Consigne de mise à jour of a document the user pilots. Asking for a new
 * index goes through a confirmation window and mails the author; the other
 * choices save at once.
 */
export function NoticeFlagSelect({
  yardId,
  documentId,
  documentLabel,
  value,
  onChanged,
}: NoticeFlagSelectProps) {
  const [selected, setSelected] = useState<NoticeFlag>(value);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => setSelected(value), [value]);

  const save = async (noticeFlag: NoticeFlagChoice) => {
    setSelected(noticeFlag);
    setSaving(true);
    try {
      const { error } = await apiClient.PATCH(
        "/api/yards/{yardId}/documents/{documentId}/notice-flag",
        {
          params: { path: { yardId, documentId: String(documentId) } },
          body: { noticeFlag },
        },
      );
      if (isNotNil(error)) {
        setSelected(value);
        toaster.error({
          title: "Consigne non modifiée",
          description: error.error,
        });
        return;
      }
      toaster.success({ title: "Modifications enregistrées" });
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <select
        aria-label={`Consigne de mise à jour ${documentLabel}`}
        title={NOTICE_FLAG_LABELS[selected]}
        value={selected}
        disabled={saving}
        onChange={(event) => {
          const choice = NOTICE_FLAG_CHOICES.find(
            (flag) => flag === event.target.value,
          );
          if (choice === "nouvel_indice") {
            setConfirming(true);
          } else if (isNotNil(choice)) {
            void save(choice);
          }
        }}
        className={cn(
          "w-full cursor-pointer rounded px-1 py-0.5 text-xs disabled:cursor-wait",
          NOTICE_FLAG_CLASSES[selected],
        )}
      >
        {selected === "new" && (
          <option value="new" disabled className={OPTION_CLASS}>
            {NOTICE_FLAG_LABELS.new}
          </option>
        )}
        {NOTICE_FLAG_CHOICES.map((flag) => (
          <option key={flag} value={flag} className={OPTION_CLASS}>
            {NOTICE_FLAG_LABELS[flag]}
          </option>
        ))}
      </select>
      <ConfirmationDialog
        open={confirming}
        title="Demander un nouvel indice"
        confirmLabel="Demander un nouvel indice"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          void save("nouvel_indice");
        }}
      >
        {`${documentLabel} passera en « Nouvel indice » : un mail demandera à son auteur d'en produire une nouvelle version, avec votre équipe en copie.`}
      </ConfirmationDialog>
    </>
  );
}
